import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { EmailVerificationBanner } from '../components/EmailVerificationBanner';
import { CreditCard, ArrowLeft, Loader2, MapPin, Check, Truck, Package, Zap, Clock, SplitSquareVertical } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIPPING_ICONS = {
  lettre_suivie: Package,
  colissimo: Truck,
  chronopost: Zap
};

const ShippingOptionCard = ({ option, selected, onSelect, testIdPrefix = "shipping" }) => {
  const Icon = SHIPPING_ICONS[option.method] || Truck;
  return (
    <label
      className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
        selected ? 'border-fuchsia-500 bg-fuchsia-500/10' : 'border-white/10 hover:border-white/20'
      }`}
      data-testid={`${testIdPrefix}-${option.method}`}
    >
      <input
        type="radio"
        name={testIdPrefix}
        value={option.method}
        checked={selected}
        onChange={() => onSelect(option.method)}
        className="sr-only"
      />
      <Icon className="w-5 h-5 text-fuchsia-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium">{option.label}</p>
        <p className="text-gray-500 text-sm">{option.description}</p>
        <p className="text-gray-500 text-xs mt-0.5">{option.delay}</p>
      </div>
      <span className="text-fuchsia-400 font-bold whitespace-nowrap">{option.price.toFixed(2)} &euro;</span>
    </label>
  );
};

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cartReady, setCartReady] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [saveAddress, setSaveAddress] = useState(true);
  const [addressLoaded, setAddressLoaded] = useState(false);
  const [shippingMethod, setShippingMethod] = useState('colissimo');
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(true);
  const [totalWeight, setTotalWeight] = useState(0);
  // Preorder delivery mode
  const [deliveryMode, setDeliveryMode] = useState('together');
  const [hasPreorder, setHasPreorder] = useState(false);
  const [hasStock, setHasStock] = useState(false);
  const [splitData, setSplitData] = useState(null);
  const [preorderShippingMethod, setPreorderShippingMethod] = useState('colissimo');
  const [shippingInfo, setShippingInfo] = useState({
    first_name: '',
    last_name: '',
    address: '',
    address2: '',
    city: '',
    postal_code: '',
    country: 'France',
    phone: ''
  });

  useEffect(() => {
    if (cart.items.length > 0) {
      setCartReady(true);
    } else if (cartReady) {
      navigate('/shop');
    }
  }, [cart.items.length, cartReady, navigate]);

  // Load saved address
  useEffect(() => {
    const loadAddress = async () => {
      try {
        const res = await axios.get(`${API}/auth/address`, { withCredentials: true });
        if (res.data.address) {
          setShippingInfo(prev => ({ ...prev, ...res.data.address }));
        }
      } catch { /* ignore */ }
      setAddressLoaded(true);
    };
    loadAddress();
  }, []);

  // Load shipping options
  useEffect(() => {
    const loadShipping = async () => {
      setShippingLoading(true);
      try {
        const res = await axios.post(`${API}/shipping/calculate`, {}, { withCredentials: true });
        setShippingOptions(res.data.options);
        setTotalWeight(res.data.total_weight);
        setHasPreorder(res.data.has_preorder || false);
        setHasStock(res.data.has_stock || false);
        setSplitData(res.data.split || null);
        if (res.data.options.length > 0 && !res.data.options.find(o => o.method === shippingMethod)) {
          setShippingMethod(res.data.options[0].method);
        }
        // Set preorder shipping default
        if (res.data.split?.preorder_options?.length > 0) {
          const found = res.data.split.preorder_options.find(o => o.method === preorderShippingMethod);
          if (!found) setPreorderShippingMethod(res.data.split.preorder_options[0].method);
        }
        // If no mixed cart, reset to together
        if (!res.data.has_preorder || !res.data.has_stock) {
          setDeliveryMode('together');
        }
      } catch {
        setShippingOptions([]);
      }
      setShippingLoading(false);
    };
    if (cartReady) loadShipping();
  }, [cartReady, cart.items.length, cart.total]);

  const isMixedCart = hasPreorder && hasStock;

  // Calculate shipping costs based on delivery mode
  const shippingCost = useMemo(() => {
    if (deliveryMode === 'split' && splitData) {
      const stockOption = splitData.stock_options?.find(o => o.method === shippingMethod);
      const preorderOption = splitData.preorder_options?.find(o => o.method === preorderShippingMethod);
      return (stockOption?.price || 0) + (preorderOption?.price || 0);
    }
    const selected = shippingOptions.find(o => o.method === shippingMethod);
    return selected?.price || 0;
  }, [deliveryMode, splitData, shippingOptions, shippingMethod, preorderShippingMethod]);

  const grandTotal = cart.total + shippingCost;

  // For display in summary
  const stockShippingCost = splitData?.stock_options?.find(o => o.method === shippingMethod)?.price || 0;
  const preorderShippingCost = splitData?.preorder_options?.find(o => o.method === preorderShippingMethod)?.price || 0;

  const handleInputChange = (e) => {
    setShippingInfo(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (!shippingInfo.first_name || !shippingInfo.last_name || !shippingInfo.address || 
        !shippingInfo.city || !shippingInfo.postal_code || !shippingInfo.phone) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    
    try {
      const originUrl = window.location.origin;
      
      const payload = { 
        origin_url: originUrl, 
        payment_method: paymentMethod,
        shipping_address: shippingInfo,
        save_address: saveAddress,
        shipping_method: shippingMethod,
        delivery_mode: deliveryMode,
      };
      if (deliveryMode === 'split') {
        payload.preorder_shipping_method = preorderShippingMethod;
      }

      const response = await axios.post(`${API}/checkout/stripe`, payload, { withCredentials: true });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
      setLoading(false);
    }
  };

  if (!cartReady) return (
    <div className="min-h-screen pt-20 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-fuchsia-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // Identify preorder items in cart for visual split
  const preorderItems = cart.items.filter(item => item.is_preorder);
  const stockItems = cart.items.filter(item => !item.is_preorder);

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/shop" className="inline-flex items-center text-gray-400 hover:text-white mb-8" data-testid="back-to-shop">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Continuer mes achats
        </Link>

        <h1 className="text-3xl font-bold text-white font-outfit mb-8">Finaliser la commande</h1>

        {/* Block checkout if email not verified */}
        {user && user.email_verified === false && (
          <div className="mb-8">
            <EmailVerificationBanner user={user} />
            <p className="text-amber-300/70 text-sm mt-3 ml-1">Vous devez verifier votre email avant de passer commande.</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left column: Shipping + Payment */}
          <div className="space-y-6">
            {/* Shipping Address */}
            <div className="bg-[#121212] rounded-2xl p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-5 h-5 text-fuchsia-400" />
                <h2 className="text-xl font-semibold text-white">Adresse de livraison</h2>
              </div>
              
              {addressLoaded && shippingInfo.first_name && (
                <div className="mb-4 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 text-xs">Adresse pre-remplie depuis votre profil</span>
                </div>
              )}
              
              <form onSubmit={handleCheckout} id="checkout-form" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name" className="text-gray-300">Prenom *</Label>
                    <Input id="first_name" name="first_name" value={shippingInfo.first_name} onChange={handleInputChange} required className="mt-1 bg-white/5 border-white/10 text-white" data-testid="shipping-firstname" />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-gray-300">Nom *</Label>
                    <Input id="last_name" name="last_name" value={shippingInfo.last_name} onChange={handleInputChange} required className="mt-1 bg-white/5 border-white/10 text-white" data-testid="shipping-lastname" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="address" className="text-gray-300">Adresse *</Label>
                  <Input id="address" name="address" value={shippingInfo.address} onChange={handleInputChange} placeholder="Numero et nom de rue" required className="mt-1 bg-white/5 border-white/10 text-white" data-testid="shipping-address" />
                </div>
                <div>
                  <Label htmlFor="address2" className="text-gray-300">Complement d'adresse</Label>
                  <Input id="address2" name="address2" value={shippingInfo.address2} onChange={handleInputChange} placeholder="Appartement, batiment, etage..." className="mt-1 bg-white/5 border-white/10 text-white" data-testid="shipping-address2" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code" className="text-gray-300">Code postal *</Label>
                    <Input id="postal_code" name="postal_code" value={shippingInfo.postal_code} onChange={handleInputChange} required className="mt-1 bg-white/5 border-white/10 text-white" data-testid="shipping-postalcode" />
                  </div>
                  <div>
                    <Label htmlFor="city" className="text-gray-300">Ville *</Label>
                    <Input id="city" name="city" value={shippingInfo.city} onChange={handleInputChange} required className="mt-1 bg-white/5 border-white/10 text-white" data-testid="shipping-city" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="country" className="text-gray-300">Pays</Label>
                    <Input id="country" name="country" value={shippingInfo.country} disabled className="mt-1 bg-white/5 border-white/10 text-gray-400" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-gray-300">Telephone *</Label>
                    <Input id="phone" name="phone" type="tel" value={shippingInfo.phone} onChange={handleInputChange} placeholder="06 12 34 56 78" required className="mt-1 bg-white/5 border-white/10 text-white" data-testid="shipping-phone" />
                  </div>
                </div>
                <label className="flex items-center gap-3 cursor-pointer mt-2" data-testid="save-address-checkbox">
                  <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${saveAddress ? 'bg-fuchsia-600 border-fuchsia-600' : 'border-white/20 bg-white/5'}`} onClick={() => setSaveAddress(!saveAddress)}>
                    {saveAddress && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-gray-300 text-sm">Sauvegarder cette adresse pour mes prochaines commandes</span>
                </label>
              </form>
            </div>

            {/* Preorder Delivery Mode Choice */}
            {isMixedCart && (
              <div className="bg-[#121212] rounded-2xl p-6 border border-amber-500/30" data-testid="delivery-mode-section">
                <div className="flex items-center gap-3 mb-2">
                  <SplitSquareVertical className="w-5 h-5 text-amber-400" />
                  <h2 className="text-xl font-semibold text-white">Mode de livraison</h2>
                </div>
                <p className="text-amber-300/70 text-sm mb-5">
                  Votre panier contient des articles en stock et des precommandes. Choisissez comment vous souhaitez recevoir votre commande.
                </p>

                <div className="space-y-3">
                  {/* Together option */}
                  <label
                    className={`block p-4 rounded-xl border cursor-pointer transition-all ${
                      deliveryMode === 'together'
                        ? 'border-fuchsia-500 bg-fuchsia-500/10 shadow-lg shadow-fuchsia-500/5'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    data-testid="delivery-mode-together"
                  >
                    <input
                      type="radio"
                      name="delivery_mode"
                      value="together"
                      checked={deliveryMode === 'together'}
                      onChange={() => setDeliveryMode('together')}
                      className="sr-only"
                    />
                    <div className="flex items-start gap-3">
                      <Package className="w-5 h-5 text-fuchsia-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-white font-semibold">Livraison unique</p>
                        <p className="text-gray-400 text-sm mt-1">
                          Tous les articles seront expedies ensemble a la sortie de la precommande. Un seul frais de port.
                        </p>
                      </div>
                    </div>
                  </label>

                  {/* Split option */}
                  <label
                    className={`block p-4 rounded-xl border cursor-pointer transition-all ${
                      deliveryMode === 'split'
                        ? 'border-fuchsia-500 bg-fuchsia-500/10 shadow-lg shadow-fuchsia-500/5'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    data-testid="delivery-mode-split"
                  >
                    <input
                      type="radio"
                      name="delivery_mode"
                      value="split"
                      checked={deliveryMode === 'split'}
                      onChange={() => setDeliveryMode('split')}
                      className="sr-only"
                    />
                    <div className="flex items-start gap-3">
                      <SplitSquareVertical className="w-5 h-5 text-fuchsia-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-white font-semibold">Livraison separee</p>
                        <p className="text-gray-400 text-sm mt-1">
                          Les articles en stock sont expedies immediatement. Les precommandes seront envoyees a leur sortie. Deux frais de port distincts.
                        </p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Shipping Method */}
            <div className="bg-[#121212] rounded-2xl p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <Truck className="w-5 h-5 text-fuchsia-400" />
                <h2 className="text-xl font-semibold text-white">
                  {deliveryMode === 'split' && isMixedCart ? 'Livraison — Articles en stock' : 'Mode de livraison'}
                </h2>
              </div>
              {deliveryMode === 'split' && isMixedCart && splitData && (
                <p className="text-gray-500 text-xs mb-4">
                  Poids articles en stock : {splitData.stock_weight >= 1000 ? `${(splitData.stock_weight / 1000).toFixed(1)} kg` : `${splitData.stock_weight} g`}
                </p>
              )}
              {deliveryMode !== 'split' && totalWeight > 0 && (
                <p className="text-gray-500 text-xs mb-4">Poids total : {totalWeight >= 1000 ? `${(totalWeight / 1000).toFixed(1)} kg` : `${totalWeight} g`}</p>
              )}

              {shippingLoading ? (
                <div className="flex items-center gap-2 text-gray-400 py-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Calcul des frais de port...
                </div>
              ) : (deliveryMode === 'split' && splitData ? splitData.stock_options : shippingOptions).length === 0 ? (
                <p className="text-red-400 text-sm">Aucune option de livraison disponible pour ce poids.</p>
              ) : (
                <div className="space-y-3">
                  {(deliveryMode === 'split' && splitData ? splitData.stock_options : shippingOptions).map((option) => (
                    <ShippingOptionCard
                      key={option.method}
                      option={option}
                      selected={shippingMethod === option.method}
                      onSelect={setShippingMethod}
                      testIdPrefix="shipping"
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Preorder Shipping Method (only when split) */}
            {deliveryMode === 'split' && isMixedCart && splitData && (
              <div className="bg-[#121212] rounded-2xl p-6 border border-amber-500/20" data-testid="preorder-shipping-section">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-5 h-5 text-amber-400" />
                  <h2 className="text-xl font-semibold text-white">Livraison — Precommandes</h2>
                </div>
                <p className="text-gray-500 text-xs mb-4">
                  Poids precommandes : {splitData.preorder_weight >= 1000 ? `${(splitData.preorder_weight / 1000).toFixed(1)} kg` : `${splitData.preorder_weight} g`}
                  {' '} — Expediees a la date de sortie
                </p>

                {splitData.preorder_options.length === 0 ? (
                  <p className="text-red-400 text-sm">Aucune option de livraison disponible pour ce poids.</p>
                ) : (
                  <div className="space-y-3">
                    {splitData.preorder_options.map((option) => (
                      <ShippingOptionCard
                        key={option.method}
                        option={option}
                        selected={preorderShippingMethod === option.method}
                        onSelect={setPreorderShippingMethod}
                        testIdPrefix="preorder-shipping"
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Payment Method */}
            <div className="bg-[#121212] rounded-2xl p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="w-5 h-5 text-fuchsia-400" />
                <h2 className="text-xl font-semibold text-white">Mode de paiement</h2>
              </div>
              <div className="space-y-3">
                <label className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${paymentMethod === 'stripe' ? 'border-fuchsia-500 bg-fuchsia-500/10' : 'border-white/10 hover:border-white/20'}`}>
                  <input type="radio" name="payment" value="stripe" checked={paymentMethod === 'stripe'} onChange={(e) => setPaymentMethod(e.target.value)} className="sr-only" />
                  <CreditCard className="w-6 h-6 text-fuchsia-400" />
                  <div>
                    <p className="text-white font-medium">Carte bancaire</p>
                    <p className="text-gray-500 text-sm">Visa, Mastercard, American Express</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${paymentMethod === 'paypal' ? 'border-fuchsia-500 bg-fuchsia-500/10' : 'border-white/10 hover:border-white/20'}`}>
                  <input type="radio" name="payment" value="paypal" checked={paymentMethod === 'paypal'} onChange={(e) => setPaymentMethod(e.target.value)} className="sr-only" />
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.11A.776.776 0 0 1 5.71 1.5h6.388c2.106 0 3.763.638 4.604 1.833.392.557.627 1.21.697 1.951.072.752-.028 1.625-.315 2.653l-.004.017v.015c-.566 2.377-1.633 4.093-3.168 5.073-1.484.949-3.382 1.368-5.726 1.368H7.04a.772.772 0 0 0-.765.651l-.799 5.052-.025.146-.008.048a.774.774 0 0 1-.367.05z" fill="#253B80"/>
                    <path d="M19.65 6.577c-.016.103-.035.208-.058.316-.69 3.541-3.053 4.768-6.07 4.768h-1.536a.742.742 0 0 0-.733.627l-.785 4.978-.223 1.412a.39.39 0 0 0 .386.453h2.712a.65.65 0 0 0 .643-.55l.026-.139.51-3.229.033-.179a.65.65 0 0 1 .642-.55h.405c2.618 0 4.666-1.063 5.266-4.139.25-1.285.121-2.358-.541-3.114a2.602 2.602 0 0 0-.677-.654z" fill="#179BD7"/>
                    <path d="M18.631 6.202a5.59 5.59 0 0 0-.697-.158 8.877 8.877 0 0 0-1.407-.103h-4.265a.65.65 0 0 0-.642.55l-.906 5.748-.026.17a.742.742 0 0 1 .733-.627h1.535c3.018 0 5.38-1.227 6.07-4.768.02-.105.04-.208.059-.312a3.703 3.703 0 0 0-.454-.5z" fill="#222D65"/>
                  </svg>
                  <div>
                    <p className="text-white font-medium">PayPal</p>
                    <p className="text-gray-500 text-sm">Paiement securise via PayPal</p>
                  </div>
                </label>
              </div>

              <Button
                type="submit"
                form="checkout-form"
                disabled={loading || shippingLoading || shippingOptions.length === 0 || (user && user.email_verified === false)}
                className="w-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white py-6 mt-6 glow-primary"
                data-testid="pay-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Redirection...
                  </>
                ) : (
                  `Payer ${grandTotal.toFixed(2)} \u20AC`
                )}
              </Button>
            </div>
          </div>

          {/* Right column: Order Summary */}
          <div>
            <div className="bg-[#121212] rounded-2xl p-6 border border-white/10 sticky top-24">
              <h2 className="text-xl font-semibold text-white mb-6">Recapitulatif</h2>
              
              {/* When split mode, show items in two groups */}
              {deliveryMode === 'split' && isMixedCart ? (
                <div className="space-y-4 max-h-80 overflow-auto">
                  {stockItems.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-fuchsia-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                        <Truck className="w-3.5 h-3.5" /> Envoi immediat
                      </p>
                      {stockItems.map((item) => (
                        <div key={item.product_id} className="flex gap-4 mb-3" data-testid={`summary-item-${item.product_id}`}>
                          <img src={item.image_url} alt={item.name} className="w-14 h-14 object-cover rounded-lg" />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white text-sm font-medium truncate">{item.name}</h4>
                            <p className="text-gray-500 text-xs">Qte: {item.quantity}</p>
                            <p className="text-fuchsia-400 font-semibold text-sm">{(item.price * item.quantity).toFixed(2)} &euro;</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {preorderItems.length > 0 && (
                    <div className="pt-3 border-t border-amber-500/20">
                      <p className="text-xs font-semibold text-amber-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" /> Precommandes
                      </p>
                      {preorderItems.map((item) => (
                        <div key={item.product_id} className="flex gap-4 mb-3" data-testid={`summary-item-${item.product_id}`}>
                          <img src={item.image_url} alt={item.name} className="w-14 h-14 object-cover rounded-lg" />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white text-sm font-medium truncate">{item.name}</h4>
                            <p className="text-gray-500 text-xs">Qte: {item.quantity}</p>
                            <p className="text-fuchsia-400 font-semibold text-sm">{(item.price * item.quantity).toFixed(2)} &euro;</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4 max-h-80 overflow-auto">
                  {cart.items.map((item) => (
                    <div key={item.product_id} className="flex gap-4" data-testid={`summary-item-${item.product_id}`}>
                      <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover rounded-lg" />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white text-sm font-medium truncate">{item.name}</h4>
                        <p className="text-gray-500 text-sm">Qte: {item.quantity}</p>
                        <p className="text-fuchsia-400 font-semibold">{(item.price * item.quantity).toFixed(2)} &euro;</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t border-white/10 mt-6 pt-6 space-y-3">
                <div className="flex justify-between text-gray-400">
                  <span>Sous-total</span>
                  <span>{cart.total.toFixed(2)} &euro;</span>
                </div>

                {deliveryMode === 'split' && isMixedCart ? (
                  <>
                    <div className="flex justify-between text-gray-400 text-sm">
                      <span>Livraison stock</span>
                      {shippingLoading ? (
                        <span className="text-gray-500 text-sm">Calcul...</span>
                      ) : (
                        <span data-testid="stock-shipping-cost">{stockShippingCost.toFixed(2)} &euro;</span>
                      )}
                    </div>
                    <div className="flex justify-between text-gray-400 text-sm">
                      <span>Livraison precommandes</span>
                      {shippingLoading ? (
                        <span className="text-gray-500 text-sm">Calcul...</span>
                      ) : (
                        <span data-testid="preorder-shipping-cost">{preorderShippingCost.toFixed(2)} &euro;</span>
                      )}
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Total livraison</span>
                      <span data-testid="shipping-cost">{shippingCost.toFixed(2)} &euro;</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-gray-400">
                    <span>Livraison ({shippingOptions.find(o => o.method === shippingMethod)?.label || '...'})</span>
                    {shippingLoading ? (
                      <span className="text-gray-500 text-sm">Calcul...</span>
                    ) : (
                      <span data-testid="shipping-cost">{shippingCost.toFixed(2)} &euro;</span>
                    )}
                  </div>
                )}

                <div className="flex justify-between text-xl font-bold text-white pt-3 border-t border-white/10">
                  <span>Total</span>
                  <span data-testid="grand-total">{grandTotal.toFixed(2)} &euro;</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
